// Revisado: 2026-02-21 [UI/UX Overhaul - Premium Aesthetic - Strict Lazy Validation]
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from './routes';
import { API_BASE_URL } from './constants';
import SEO from './components/shared/SEO';

// --- VISUAL RESOURCES ---
const ART_IDS = {
    hero: 30098,
    hero_secondary: 30110,
    tools: 30252,
    database: 30179,
    skills: 30281,
};

const CARD_IDS = [30146, 30073, 30164, 30039, 30111];

const getThumbUrl = (id, width = 800) =>
    `${API_BASE_URL}/api/thumb?img=/images/Support/Art/Support_Card_${id}.png&width=${width}`;

const getCardUrl = (id, width = 512) =>
    `${API_BASE_URL}/api/thumb?img=/images/Support/Card/Support_Card_${id}_Card.png&width=${width}`;

// --- HELPERS ---

// Hook for Scroll Reveal logic using IntersectionObserver
const useScrollReveal = (threshold = 0.1) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                observer.unobserve(entry.target);
            }
        }, { threshold });

        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);

    return [ref, isVisible];
};

const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const ImageWithLoader = ({ src, alt, className = "", style = {}, priority = false, loadTrigger = true, ...props }) => {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
        <img
            // BLOQUEO DE RED: Si loadTrigger es fall, el navegador NUNCA verá el URL original ni lo descargará.
            src={loadTrigger ? src : TRANSPARENT_PIXEL}
            alt={alt}
            className={`transition-opacity duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${className}`}
            style={{ opacity: isLoaded ? "" : 0, ...style }}
            onLoad={(e) => {
                // Evitamos detonar animaciones por cargar el GIF transparente
                if (loadTrigger && e.target.src !== TRANSPARENT_PIXEL) {
                    setIsLoaded(true);
                }
            }}
            loading={priority ? "eager" : undefined}
            fetchPriority={priority ? "high" : undefined}
            {...props}
        />
    );
};

// --- MAIN COMPONENT ---
const Welcome = () => {
    const { t } = useTranslation();

    // Scroll Reveals for sections.
    // 'Visible' state is now strictly tied to the ImageLoader src trigger.
    const [heroRef, heroVisible] = useScrollReveal(0.05);
    const [toolsRef, toolsVisible] = useScrollReveal(0.15);
    const [compRef, compVisible] = useScrollReveal(0.15);
    const [dbRef, dbVisible] = useScrollReveal(0.15);
    const [skillsRef, skillsVisible] = useScrollReveal(0.2);

    return (
        <main className="relative bg-[#0a0f16] text-white overflow-x-hidden font-sans selection:bg-[#00c541] selection:text-white">
            <SEO title={t('MetaWelcome.title')} description={t('MetaWelcome.description')} />

            {/* Global Noise Texture (Subtle Grain) */}
            <div className="fixed inset-0 z-50 opacity-[0.015] pointer-events-none mix-blend-overlay" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")' }}></div>

            {/* =====================================================================================
                SECTION 1: HERO (The Core Impact)
            ===================================================================================== */}
            <section ref={heroRef} className="relative h-screen w-full flex items-center justify-center overflow-hidden">
                {/* Background Composition */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f16] via-[#0a0f16]/60 to-[#0a0f16]/10 z-10"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f16] via-[#0a0f16]/80 to-transparent z-10"></div>
                    <div className="absolute top-1/2 left-1/4 w-[800px] h-[800px] bg-[#00c541]/10 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none animate-pulse" style={{ animationDuration: '6s' }}></div>

                    <ImageWithLoader
                        src={getThumbUrl(ART_IDS.hero, 800)}
                        alt="Hero Background Main"
                        className="absolute inset-0 w-full h-full object-cover object-[75%_5%] origin-top opacity-50"
                        style={{ transform: 'scale(-1.05, 1.05)' }}
                        priority={true}
                        loadTrigger={true} // El Hero arranca SIEMPRE al segundo 0 sin depender del scroll.
                    />
                </div>

                {/* Hero Content */}
                <div className="relative z-20 container mx-auto px-6 lg:px-12 flex flex-col items-start justify-center h-full pt-20">
                    <div className={`flex items-center gap-4 mb-6 transition-all duration-1000 delay-300 ease-out ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                        <div className="h-[2px] w-12 bg-gradient-to-r from-[#00c541] to-emerald-300"></div>
                        <span className="text-emerald-400 text-xs font-mono font-bold uppercase tracking-[0.4em] drop-shadow-[0_0_5px_rgba(52,211,153,0.5)]">
                            {t('Welcome.system_status')}
                        </span>
                    </div>

                    <h1 className={`text-6xl md:text-8xl lg:text-9xl font-black italic tracking-tighter leading-[0.85] mb-8 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
                        <span className="block text-transparent bg-clip-text bg-gradient-to-br from-white via-slate-200 to-slate-500 drop-shadow-2xl">
                            {t('Welcome.hero_title_1')}
                        </span>
                        <span className="block text-white drop-shadow-[0_0_35px_rgba(0,197,65,0.4)] relative overflow-hidden inline-[block]">
                            {t('Welcome.hero_title_2')}
                            <div className="absolute top-0 left-0 h-full w-[200%] bg-gradient-to-r from-transparent via-emerald-100/30 to-transparent -skew-x-[20deg] animate-[scan_3s_ease-in-out_infinite] pointer-events-none"></div>
                        </span>
                    </h1>

                    <p className={`text-lg md:text-xl lg:text-2xl text-slate-300 max-w-2xl font-light leading-relaxed mb-12 border-l-[3px] border-emerald-500/50 pl-6 transition-all duration-1000 delay-500 ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        {t('Welcome.hero_desc')}
                    </p>

                    <Link
                        to={ROUTES.TOOLS_SCORE}
                        className={`group relative flex items-center gap-4 px-10 py-5 bg-gradient-to-b from-[#00d847] to-[#00b23a] text-white rounded-full font-black uppercase tracking-widest transition-all duration-500 hover:scale-[1.03] shadow-[0_0_30px_rgba(0,197,65,0.3)] hover:shadow-[0_0_40px_rgba(0,197,65,0.6)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} delay-700`}
                    >
                        <div className="absolute inset-0 rounded-full border border-white/30 group-hover:border-white/60 transition-colors"></div>
                        <span className="relative z-10">{t('Welcome.cta_start')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="relative z-10 h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </Link>
                </div>

                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 animate-bounce text-emerald-500/50 pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </section>

            {/* =====================================================================================
                SECTION 2: TOOLS (Calculator) - Right Oriented
            ===================================================================================== */}
            <section ref={toolsRef} id="tools" className="relative min-h-[80vh] w-full flex flex-col md:flex-row bg-[#080c12] border-t border-slate-800/50 overflow-hidden">
                <div className="relative w-full md:w-1/2 h-[50vh] md:h-auto order-1 md:order-2 group overflow-hidden">
                    <div className="absolute inset-0 bg-blue-950/40 z-10 mix-blend-multiply group-hover:bg-transparent transition-colors duration-700"></div>
                    <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-l from-transparent via-[#080c12]/50 to-[#080c12] z-20"></div>

                    <ImageWithLoader
                        src={getThumbUrl(ART_IDS.tools, 800)}
                        alt="Tools Art"
                        className="w-full h-full object-cover object-center transition-transform duration-[1.5s] ease-out group-hover:scale-105"
                        loadTrigger={toolsVisible} // CARGA RETRASADA: Solo iniciará descarga si el usuario baja a la sección 2.
                    />

                    <div className={`absolute top-12 right-12 z-30 p-5 bg-[#0a0f16]/60 backdrop-blur-xl rounded-2xl border border-white/10 shadow-[0_20px_40px_rgba(0,0,0,0.5)] transition-all duration-1000 ${toolsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
                        <div className="absolute inset-0 border border-blue-400/20 rounded-2xl animate-pulse"></div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="text-blue-400 w-10 h-10 mb-3 mx-auto drop-shadow-[0_0_10px_rgba(96,165,250,0.5)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <div className="text-xl font-mono font-bold text-white tracking-wide">{t('Welcome.badge_best_ranks')}</div>
                    </div>
                </div>

                <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-24 order-2 md:order-1 relative z-30">
                    <div className="max-w-xl">
                        <div className={`flex items-center gap-4 mb-8 transition-all duration-1000 delay-100 ${toolsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                            <span className="text-[10rem] font-black text-white/5 absolute -left-16 -top-24 select-none leading-none tracking-tighter mix-blend-screen">01</span>
                            <span className="text-blue-500 font-bold uppercase tracking-[0.2em] text-sm relative">{t('Welcome.tools_utility_label')}</span>
                        </div>

                        <h2 className={`text-5xl md:text-7xl lg:text-8xl font-black text-white italic tracking-tighter mb-8 leading-[0.85] transition-all duration-1000 delay-200 ${toolsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            {t('Welcome.tools_title_1')} <br />
                            <span className="relative inline-block mt-2">
                                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 drop-shadow-xl">{t('Welcome.tools_title_2')}</span>
                                <div className="absolute -bottom-2 left-0 w-full h-3 bg-blue-500/20 rounded-full blur-md"></div>
                            </span>
                        </h2>

                        <p className={`text-slate-400 text-lg md:text-xl font-light mb-10 leading-relaxed transition-all duration-1000 delay-300 ${toolsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            {t('Welcome.tools_desc')}
                        </p>

                        <div className={`transition-all duration-1000 delay-400 ${toolsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            <Link to={ROUTES.TOOLS_SCORE} className="inline-flex items-center gap-3 text-white border-b border-blue-500/50 pb-2 hover:border-blue-400 transition-colors font-bold text-lg uppercase tracking-widest group">
                                {t('Welcome.tools_cta')}
                                <span className="p-2 bg-blue-500/10 rounded-full group-hover:bg-blue-500/20 transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </span>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* =====================================================================================
                SECTION 3: TOOLS (Comparator) - Left Oriented
            ===================================================================================== */}
            <section ref={compRef} className="relative min-h-[80vh] w-full flex flex-col md:flex-row bg-[#0a0f16] border-t border-slate-800/50 overflow-hidden">
                <div className="relative w-full md:w-1/2 h-[50vh] md:h-auto order-1 md:order-1 group overflow-hidden">
                    <div className="absolute inset-0 bg-cyan-950/40 z-10 mix-blend-multiply group-hover:bg-transparent transition-colors duration-700"></div>
                    <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-transparent via-[#0a0f16]/50 to-[#0a0f16] z-20"></div>

                    <ImageWithLoader
                        src={getThumbUrl(ART_IDS.hero_secondary, 800)}
                        alt="Comparator Art"
                        className="w-full h-full object-cover object-center transition-transform duration-[1.5s] ease-out group-hover:scale-105"
                        loadTrigger={compVisible} // CARGA RETRASADA
                    />

                    <div className={`absolute top-12 left-12 z-30 p-5 bg-[#080c12]/60 backdrop-blur-xl rounded-2xl border border-white/10 shadow-[0_20px_40px_rgba(0,0,0,0.5)] transition-all duration-1000 ${compVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
                        <div className="absolute inset-0 border border-cyan-400/20 rounded-2xl animate-pulse"></div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="text-cyan-400 w-10 h-10 mb-3 mx-auto drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        <div className="text-xl font-mono font-bold text-white tracking-wide">{t('Welcome.badge_best_decks')}</div>
                    </div>
                </div>

                <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-24 order-2 md:order-2 relative z-30">
                    <div className="max-w-xl text-left">
                        <div className={`flex items-center gap-4 mb-8 md:justify-start transition-all duration-1000 delay-100 ${compVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                            <span className="text-[10rem] font-black text-white/5 absolute -right-16 -top-24 select-none leading-none tracking-tighter mix-blend-screen md:left-auto md:-right-16">02</span>
                            <span className="text-cyan-400 font-bold uppercase tracking-[0.2em] text-sm relative">{t('Welcome.tools_utility_label')}</span>
                        </div>

                        <h2 className={`text-5xl md:text-7xl lg:text-8xl font-black text-white italic tracking-tighter mb-8 leading-[0.85] transition-all duration-1000 delay-200 ${compVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            {t('Welcome.tools_support_title_1')} <br />
                            <span className="relative inline-block mt-2">
                                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-300 drop-shadow-xl">{t('Welcome.tools_support_title_2')}</span>
                                <div className="absolute -bottom-2 left-0 w-full h-3 bg-cyan-500/20 rounded-full blur-md"></div>
                            </span>
                        </h2>

                        <p className={`text-slate-400 text-lg md:text-xl font-light mb-10 leading-relaxed transition-all duration-1000 delay-300 ${compVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            {t('Welcome.tools_support_desc')}
                        </p>

                        <div className={`transition-all duration-1000 delay-400 ${compVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            <Link to={ROUTES.TOOLS_SUPPORT} className="inline-flex items-center gap-3 text-white border-b border-cyan-500/50 pb-2 hover:border-cyan-400 transition-colors font-bold text-lg uppercase tracking-widest group">
                                {t('Welcome.tools_support_cta')}
                                <span className="p-2 bg-cyan-500/10 rounded-full group-hover:bg-cyan-500/20 transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </span>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* =====================================================================================
                SECTION 4: DATABASE & CARDS 3D ENGINE
            ===================================================================================== */}
            <section ref={dbRef} className="relative min-h-screen w-full py-32 flex items-center overflow-hidden bg-[#05080c] border-t border-slate-900">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#05080c] via-[#05080c]/80 to-[#05080c]/40 z-10 backdrop-blur-[2px]"></div>
                    <ImageWithLoader
                        src={getThumbUrl(ART_IDS.database, 800)}
                        alt="Database BG"
                        className="w-full h-full object-cover object-right-top opacity-50"
                        loadTrigger={dbVisible} // CARGA RETRASADA (Sexteto pesado: 1 bg + 5 cards)
                    />
                </div>

                <div className="relative z-20 container mx-auto px-6 lg:px-12 flex flex-col xl:flex-row items-center gap-20">

                    <div className="w-full xl:w-1/3">
                        <div className={`mb-6 flex items-center gap-3 transition-all duration-1000 ${dbVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="text-amber-400 h-6 w-6 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                            </svg>
                            <span className="text-amber-400 font-bold uppercase tracking-[0.2em] text-sm">{t('Welcome.db_archive_label')}</span>
                        </div>

                        <h2 className={`text-6xl md:text-7xl lg:text-8xl font-black text-white italic tracking-tighter mb-8 leading-[0.85] transition-all duration-1000 delay-200 ${dbVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            {t('Welcome.db_title_1')} <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400 drop-shadow-lg relative">
                                {t('Welcome.db_title_2')}
                            </span>
                        </h2>

                        <p className={`text-slate-400 text-lg md:text-xl font-light mb-12 leading-relaxed transition-all duration-1000 delay-300 ${dbVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            {t('Welcome.db_desc')}
                        </p>

                        <div className="flex flex-col gap-4">
                            {[
                                { to: ROUTES.CHARACTERS_ORIGINAL, icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', text: t('Welcome.db_char'), delay: 'delay-400' },
                                { to: ROUTES.CHARACTERS_SUPPORT, icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', text: t('Welcome.db_support'), delay: 'delay-500' }
                            ].map((btn, idx) => (
                                <div key={idx} className={`transition-all duration-1000 ${btn.delay} ${dbVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                                    <Link to={btn.to} className="flex items-center justify-between p-5 bg-[#0f172a]/40 hover:bg-[#0f172a] border border-slate-700/50 hover:border-amber-400/50 rounded-2xl transition-all duration-300 group backdrop-blur-md relative overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-r from-amber-400/0 to-amber-400/5 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500 ease-out"></div>
                                        <span className="font-bold text-white flex items-center gap-4 text-lg relative z-10">
                                            <div className="p-2 bg-amber-400/10 rounded-lg group-hover:bg-amber-400/20 transition-colors">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={btn.icon} />
                                                </svg>
                                            </div>
                                            {btn.text}
                                        </span>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-1 transition-all relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={`w-full xl:w-2/3 h-[400px] md:h-[600px] relative perspective-[1200px] flex items-center justify-center xl:justify-end [--x-spread:50px] [--y-spread:15px] [--rot-spread:10deg] md:[--x-spread:120px] md:[--y-spread:30px] md:[--rot-spread:12deg] transition-all duration-[1.5s] ease-[cubic-bezier(0.16,1,0.3,1)] delay-300 ${dbVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
                        {CARD_IDS.map((id, index) => {
                            return (
                                <div
                                    key={id}
                                    className="absolute w-28 md:w-64 transition-all duration-500 ease-out hover:z-[60] hover:scale-125 hover:-translate-y-16 cursor-[url('data:image/svg+xml;utf8,<svg xmlns=\\'http://www.w3.org/2000/svg\\' width=\\'24\\' height=\\'24\\' viewBox=\\'0 0 24 24\\'><circle cx=\\'12\\' cy=\\'12\\' r=\\'10\\' fill=\\'%23fbbf24\\' opacity=\\'0.5\\'/></svg>'),_auto] shadow-[0_20px_40px_rgba(0,0,0,0.8)] hover:shadow-[0_40px_80px_rgba(251,191,36,0.3)] filter brightness-75 hover:brightness-110"
                                    style={{
                                        '--i': index,
                                        '--abs-diff': Math.abs(index - 2),
                                        transform: 'translateX(calc(-50% + (var(--i) - 2) * var(--x-spread))) translateY(calc(var(--abs-diff) * var(--y-spread))) rotate(calc((var(--i) - 2) * var(--rot-spread))) rotateY(calc((var(--i) - 2) * 5deg))',
                                        zIndex: index + 10,
                                        left: '50%'
                                    }}
                                >
                                    <ImageWithLoader
                                        src={getCardUrl(id)}
                                        alt={t('Welcome.alt_card', { id: id })}
                                        className="w-full rounded-2xl border border-white/20 backdrop-blur-sm"
                                        loadTrigger={dbVisible} // CARGA RETRASADA: El navegador ni sabrá la URL de las cartas hasta el scroll.
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 rounded-2xl opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* =====================================================================================
                SECTION 5: SKILLS BANNER
            ===================================================================================== */}
            <section ref={skillsRef} className="relative h-[80vh] w-full flex items-center justify-center overflow-hidden group bg-black border-t border-slate-900">
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent z-10 opacity-70 group-hover:opacity-30 transition-opacity duration-1000 ease-out"></div>

                <div className="absolute inset-0 mix-blend-color z-0 bg-[#00c541] opacity-10 group-hover:opacity-0 transition-opacity duration-1000 pointer-events-none"></div>

                <ImageWithLoader
                    src={getThumbUrl(ART_IDS.skills, 800)}
                    alt="Skills Art"
                    className="absolute inset-0 w-full h-full object-cover object-[50%_15%] origin-top scale-110 transition-transform duration-[1.5s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-100 opacity-60 group-hover:opacity-100 filter contrast-[1.1]"
                    loadTrigger={skillsVisible} // CARGA RETRASADA
                />

                <div className="relative z-20 text-center max-w-5xl px-6 flex flex-col items-center">
                    <div className={`transition-all duration-1000 ${skillsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="text-white mx-auto mb-8 drop-shadow-[0_0_15px_rgba(255,255,255,0.5)] w-20 h-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                    </div>

                    <h2 className={`text-7xl md:text-8xl lg:text-[10rem] font-black text-white italic tracking-tighter mb-6 drop-shadow-[0_20px_40px_rgba(0,0,0,0.8)] leading-[0.8] transition-all duration-1000 delay-100 ${skillsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        {t('Welcome.skills_title')}
                    </h2>

                    <p className={`text-xl md:text-3xl text-slate-200 font-light mb-12 drop-shadow-lg tracking-wide transition-all duration-1000 delay-200 ${skillsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        {t('Welcome.skills_desc')}
                    </p>

                    <div className={`transition-all duration-1000 delay-300 ${skillsVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                        <Link to={ROUTES.SKILLS} className="group/btn relative inline-flex items-center gap-4 px-12 py-5 overflow-hidden rounded-full font-black uppercase tracking-[0.2em] transition-all duration-500">
                            <div className="absolute inset-0 bg-white/5 backdrop-blur-md border border-white/20 transition-colors duration-500 group-hover/btn:bg-white group-hover/btn:border-white"></div>
                            <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 bg-gradient-to-r from-emerald-400/0 via-emerald-400/20 to-emerald-400/0 blur-md transition-opacity duration-500"></div>

                            <span className="relative z-10 text-white group-hover/btn:text-black transition-colors duration-500 text-lg">
                                {t('Welcome.skills_cta')}
                            </span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="relative z-10 h-6 w-6 text-white group-hover/btn:text-black group-hover/btn:translate-x-2 transition-all duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Custom Keyframes for the UI Text Shimmer */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes scan {
                    0% { transform: translateX(-150%) skewX(-20deg); }
                    100% { transform: translateX(150%) skewX(-20deg); }
                }
            `}} />
        </main>
    );
};

export default Welcome;
